import { useCallback, useEffect, useState } from "react";
import { fetchProtocolHealth } from "../services/api.js";

/**
 * Single-call aggregate of every cross-module status surface — treasury
 * reserves, SafeVault accounting, loan exposure, solvency-floor
 * coverage, peg flag, dormant/abandoned counts, open critical/warning
 * alert totals.
 *
 * Used by the Dashboard "system status" tile and the global
 * PausedDepositsBanner. Polls every 30s by default (deposits-paused is
 * a chain flag that flips rarely; the banner doesn't need second-level
 * latency).
 */
export function useProtocolHealth({ pollMs = 30_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const api = await fetchProtocolHealth();
            setData(api);
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch protocol health");
            // Don't clear stale data on a transient failure — keep
            // whatever we last showed so the banner doesn't flicker.
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const id = setInterval(refresh, pollMs);
        return () => clearInterval(id);
    }, [refresh, pollMs]);

    return { data, isLoading, error, refresh };
}
